import dayjs, { Dayjs } from "dayjs";
import { datetime } from "src/utils";

// BE day-index convention (0 = Monday ... 6 = Sunday) - see formatPeriod.ts, which
// duplicates the same table for the read-side column and explains why it isn't a
// shared import.
export const DAY_ORDER = ["0", "1", "2", "3", "4", "5", "6"];

export type WebsiteSystemTypeTime = "now" | "schedule" | "custom";

// Loaded from a GET row (deserializeScheduleForm) and edited in the modal. Holds
// PLAIN (non-tz-tagged) dayjs values carrying the Bangkok wall-clock digits -
// matching exactly what src/components/DatePicker itself produces (its dayjs
// generateConfig has no .tz() awareness at all), so a loaded value and a
// freshly-picked value stay comparable with .isSame()/.isAfter() without a
// tz-tag mismatch. Serializing to the wire (toNaiveUtcString) and the
// not-in-the-past check both go through an explicit Bangkok reinterpretation
// instead of relying on these values' own (non-)timezone.
export type ScheduleFormValue = {
  typeTime: WebsiteSystemTypeTime;
  daysWeek: string[];
  scheduleStart?: Dayjs;
  scheduleEnd?: Dayjs;
  customStart?: Dayjs;
  customEnd?: Dayjs;
};

export type SerializedSchedule = {
  type_time: WebsiteSystemTypeTime;
  days_week?: string;
  start_datetime?: string;
  end_datetime?: string;
};

// Inverse of src/utils/datetime.ts's `datetime()` (dayjs.utc(value).tz()): takes a
// dayjs value carrying Bangkok wall-clock digits (as the picker holds it,
// tz-tagged or not - only .format() is read) and returns the naive-UTC
// "YYYY-MM-DD HH:mm:ss" string the backend stores - see _to_naive_utc /
// convert_time_zone_thai in backoffice-backend's services/v1/website_system.py and
// services/v1/feature_toggle.py. Built on dayjs.tz(str, "Asia/Bangkok").utc() (not
// a bare "subtract 7 hours"), so it stays correct even if the zone ever picks up a
// DST-like offset change - verified against a plain -7h arithmetic equivalent
// under three different process TZ values (+7, UTC, America/New_York) before
// relying on it here.
export const toNaiveUtcString = (value: Dayjs): string =>
  dayjs.tz(value.format("YYYY-MM-DD HH:mm:ss"), "Asia/Bangkok").utc().format("YYYY-MM-DD HH:mm:ss");

// Loads a GET row's stored schedule into form-ready values. Reads the stored
// naive-UTC string through datetime() (Bangkok-tz-tagged) once, purely to get the
// right wall-clock digits, then re-wraps that formatted string in a plain dayjs()
// - see the ScheduleFormValue comment for why plain. Uses the stored value as-is
// (not re-dated to today, unlike the read-only table column's formatWebsiteSystemPeriod):
// re-dating here would mean a save that only touched the message text also
// silently rewrote start_datetime/end_datetime to today, producing spurious audit
// rows for fields nobody edited.
const toPlainLocal = (storedNaiveUtc: string): Dayjs => dayjs(datetime(storedNaiveUtc).format("YYYY-MM-DD HH:mm:ss"));

export const deserializeScheduleForm = (row: {
  typeTime: string;
  daysWeek: string[];
  startDatetime?: string;
  endDatetime?: string;
}): ScheduleFormValue => {
  const typeTime = (row.typeTime as WebsiteSystemTypeTime) || "now";
  const start = row.startDatetime ? toPlainLocal(row.startDatetime) : undefined;
  const end = row.endDatetime ? toPlainLocal(row.endDatetime) : undefined;
  return {
    typeTime,
    daysWeek: row.daysWeek,
    scheduleStart: typeTime === "schedule" ? start : undefined,
    scheduleEnd: typeTime === "schedule" ? end : undefined,
    customStart: typeTime === "custom" ? start : undefined,
    customEnd: typeTime === "custom" ? end : undefined,
  };
};

// The inverse of deserializeScheduleForm - sends the times that matter for the
// selected type (see _validate_schedule / update_website_system_service in the
// backend, which reads exactly this per-type field set), so switching radios and
// saving never resends a stale time left over from a different type.
//
// The fields a type does not use are the exception: the backend writes whatever
// the PUT carries, so a row that still holds a day list or times from an earlier
// type would lose them on a save that changed nothing - and get audit entries for
// them. `stored` (the row's values exactly as the API returned them) is sent back
// unchanged instead, as the page this replaced did: days_week for "now" and
// "custom", start/end for "now". An empty stored value is left out (stored as
// null either way; the backend refuses "" as a day list).
export type StoredScheduleFields = {
  days_week?: string | null;
  start_datetime?: string | null;
  end_datetime?: string | null;
};

export const serializeScheduleForm = (form: ScheduleFormValue, stored?: StoredScheduleFields): SerializedSchedule => {
  const keptDaysWeek = stored?.days_week || undefined;
  if (form.typeTime === "schedule") {
    return {
      type_time: "schedule",
      // Not sorted: the old page stored days in click order (e.g. "6,0,1"), and
      // toggleDay in the modal already appends a newly-checked day to the end of
      // the array while preserving the rest - so joining as-is keeps an
      // unchanged row's days_week byte-identical on save (no sort-induced
      // reordering that would otherwise look like an edit in the audit log).
      days_week: form.daysWeek.join(","),
      start_datetime: form.scheduleStart ? toNaiveUtcString(form.scheduleStart) : undefined,
      end_datetime: form.scheduleEnd ? toNaiveUtcString(form.scheduleEnd) : undefined,
    };
  }
  if (form.typeTime === "custom") {
    return {
      type_time: "custom",
      days_week: keptDaysWeek,
      start_datetime: form.customStart ? toNaiveUtcString(form.customStart) : undefined,
      end_datetime: form.customEnd ? toNaiveUtcString(form.customEnd) : undefined,
    };
  }
  return {
    type_time: "now",
    days_week: keptDaysWeek,
    start_datetime: stored?.start_datetime || undefined,
    end_datetime: stored?.end_datetime || undefined,
  };
};

export type ValidateScheduleFormOptions = {
  // True when the currently-entered custom start is unchanged from the row's
  // original stored start - mirrors the backend's own exemption in
  // _validate_schedule (services/v1/website_system.py): editing the text of an
  // already-live custom window must not be blocked just because its start is now
  // in the past.
  customStartUnchanged: boolean;
  // Also require a custom end that is still ahead of now. The "renew the range
  // before switching on" dialog always sets this. The edit modal sets it while the
  // row is switched on: saving an ended window on a live row makes the backend
  // switch the row off in that same save. A row that is off may still keep an
  // ended window.
  requireFutureEnd?: boolean;
};

// Client-side mirror of the backend's own _validate_schedule, so an admin sees the
// same rejection before submitting instead of only after a round trip. The
// backend is still authoritative - see WebsiteSystemEditModal, which also surfaces
// a 400's service_message inline.
export const validateScheduleForm = (
  form: ScheduleFormValue,
  options: ValidateScheduleFormOptions
): string | undefined => {
  if (form.typeTime === "schedule") {
    if (form.daysWeek.length === 0) return "กรุณาเลือกอย่างน้อยหนึ่งวัน";
    if (!form.scheduleStart || !form.scheduleEnd) return "กรุณาระบุเวลาเริ่มต้นและเวลาสิ้นสุด";
    // Overnight (start-of-day time later than end-of-day time, e.g. 22:00->02:00)
    // is allowed - only an exactly-equal pair is rejected as a zero-length window.
    if (form.scheduleStart.format("HH:mm:ss") === form.scheduleEnd.format("HH:mm:ss")) {
      return "เวลาเริ่มต้นและเวลาสิ้นสุดต้องไม่เท่ากัน";
    }
    return undefined;
  }
  if (form.typeTime === "custom") {
    if (!form.customStart || !form.customEnd) return "กรุณาระบุวันและเวลาเริ่มต้น-สิ้นสุด";
    if (!form.customEnd.isAfter(form.customStart)) return "วันเวลาสิ้นสุดต้องอยู่หลังวันเวลาเริ่มต้น";
    if (options.requireFutureEnd) {
      // Same UTC-instant string comparison as the start check below.
      const endUtc = toNaiveUtcString(form.customEnd);
      const nowUtc = dayjs.utc().format("YYYY-MM-DD HH:mm:ss");
      if (endUtc <= nowUtc) return "วันเวลาสิ้นสุดต้องเป็นเวลาในอนาคต";
    }
    if (!options.customStartUnchanged) {
      // Compares real UTC instants (not the possibly browser-timezone-dependent
      // form values directly against a bare dayjs()) - toNaiveUtcString already
      // reinterprets the picker's wall-clock digits as Bangkok, and the fixed
      // "YYYY-MM-DD HH:mm:ss" width makes lexicographic string comparison agree
      // with chronological order. A changed start must clear now by a minute:
      // the backend refuses a changed start that is in the past by the time the
      // request lands, so a clock a little off or a slow request must not be
      // able to trip it.
      const startUtc = toNaiveUtcString(form.customStart);
      const minStartUtc = dayjs.utc().add(1, "minute").format("YYYY-MM-DD HH:mm:ss");
      if (startUtc < minStartUtc) return "วันเวลาเริ่มต้นต้องอยู่หลังเวลาปัจจุบันอย่างน้อย 1 นาที";
    }
    return undefined;
  }
  return undefined;
};

// Whether a row's one-off custom window has already ended - the same question
// the backend answers before refusing to switch such a row on
// (_custom_window_ended in services/v1/website_system.py): a custom row with an
// end, and that end strictly before now. An end exactly at now is not ended yet,
// and a custom row with no end never ends. Reads the stored naive-UTC end
// through datetime() like the rest of the page.
export const isCustomRangeEnded = (
  row: { typeTime: string; endDatetime?: string },
  now: Dayjs = dayjs()
): boolean => {
  if (row.typeTime !== "custom" || !row.endDatetime) return false;
  return datetime(row.endDatetime).isBefore(now);
};
